export type MapPopupSafeInsets = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

export type MapCardFocusOptions = {
  preferredGap?: number;
  focusBias?: number;
  safeInsets: MapPopupSafeInsets;
  onProgrammaticMoveChange?: (active: boolean) => void;
};

type LatLngLike = { lat: number; lng: number };

const clampNumber = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

/**
 * Keep a transient marker-owned card inside Big Blue's safe viewport without
 * changing the marker's canonical geographic coordinate. Only the camera pans.
 */
export const focusMapCardNearMarker = (
  map: any,
  marker: any,
  cardEl: HTMLElement | null,
  opts: MapCardFocusOptions,
) => {
  if (!map || !marker || !cardEl) return;

  const size = map.getSize?.();
  const mapEl = map.getContainer?.() as HTMLElement | null;
  const markerLatLng = marker.getLatLng?.();
  if (!size || !mapEl || !markerLatLng) return;

  const inset = opts.safeInsets;
  const gap = Math.max(18, Number(opts.preferredGap || 22));
  const focusBias = clampNumber(Number(opts.focusBias || 0.68), 0.5, 0.86);

  try {
    const markerPoint = map.latLngToContainerPoint(markerLatLng);
    const safeLeft = inset.left + gap;
    const safeRight = size.x - inset.right - gap;
    const safeTop = inset.top + gap;
    const safeBottom = size.y - inset.bottom - gap;

    const horizontalPad = Math.min(118, Math.max(72, size.x * (1 - focusBias)));
    const verticalPad = Math.min(132, Math.max(78, size.y * (1 - focusBias)));
    const focusLeft = Math.max(safeLeft, horizontalPad);
    const focusRight = Math.min(safeRight, size.x - horizontalPad);
    const focusTop = Math.max(safeTop, inset.top + verticalPad * 0.5);
    const focusBottom = Math.min(
      safeBottom,
      size.y - inset.bottom - verticalPad * 0.5,
    );

    let desiredScreenShiftX = 0;
    let desiredScreenShiftY = 0;

    if (markerPoint.x < focusLeft) desiredScreenShiftX = focusLeft - markerPoint.x;
    else if (markerPoint.x > focusRight) desiredScreenShiftX = focusRight - markerPoint.x;

    if (markerPoint.y < focusTop) desiredScreenShiftY = focusTop - markerPoint.y;
    else if (markerPoint.y > focusBottom) desiredScreenShiftY = focusBottom - markerPoint.y;

    const mapRect = mapEl.getBoundingClientRect();
    const cardRect = cardEl.getBoundingClientRect();
    const cardLeft = cardRect.left - mapRect.left;
    const cardRight = cardRect.right - mapRect.left;
    const cardTop = cardRect.top - mapRect.top;
    const cardBottom = cardRect.bottom - mapRect.top;

    const clipLeft = Math.max(0, safeLeft - cardLeft);
    const clipRight = Math.max(0, cardRight - safeRight);
    const clipTop = Math.max(0, safeTop - cardTop);
    const clipBottom = Math.max(0, cardBottom - safeBottom);

    if (clipLeft > 0) desiredScreenShiftX = Math.max(desiredScreenShiftX, clipLeft + gap);
    if (clipRight > 0) desiredScreenShiftX = Math.min(desiredScreenShiftX, -(clipRight + gap));
    if (clipTop > 0) desiredScreenShiftY = Math.max(desiredScreenShiftY, clipTop + gap);
    if (clipBottom > 0) desiredScreenShiftY = Math.min(desiredScreenShiftY, -(clipBottom + gap));

    desiredScreenShiftX = clampNumber(
      desiredScreenShiftX,
      -Math.min(180, size.x * 0.34),
      Math.min(180, size.x * 0.34),
    );
    desiredScreenShiftY = clampNumber(
      desiredScreenShiftY,
      -Math.min(170, size.y * 0.26),
      Math.min(170, size.y * 0.26),
    );

    if (Math.abs(desiredScreenShiftX) < 3 && Math.abs(desiredScreenShiftY) < 3) return;

    opts.onProgrammaticMoveChange?.(true);
    map.panBy?.(
      [-desiredScreenShiftX, -desiredScreenShiftY],
      { animate: true, duration: 0.22, easeLinearity: 0.35 },
    );
    window.setTimeout(() => opts.onProgrammaticMoveChange?.(false), 320);
  } catch {
    opts.onProgrammaticMoveChange?.(false);
  }
};

/**
 * Place the selected map-action anchor in the upper-center presentation zone,
 * leaving the lower viewport clear for Search / Log / Save controls.
 */
export const focusMapActionAnchor = (map: any, anchor: LatLngLike) => {
  try {
    const size = map?.getSize?.();
    const point = map?.latLngToContainerPoint?.([anchor.lat, anchor.lng]);
    if (!size || !point) return;

    const desiredX = size.x * 0.5;
    const desiredY = Math.max(170, Math.min(285, size.y * 0.33));
    const dx = point.x - desiredX;
    const dy = point.y - desiredY;
    if (Math.abs(dx) < 2 && Math.abs(dy) < 2) return;

    map.panBy?.([dx, dy], { animate: true, duration: 0.20 });
  } catch {}
};
